import { Stack } from '@mui/material';
import { InputWithStatus } from 'components/Form/FormControlVerificationStatus/Input';
import { FormControlWithStatus } from 'components/Form/FormControlVerificationStatus';
import { IDriverCdlFields } from 'common/document/ui/driver-document';
import { MultipleSelectWithStatus } from 'components/Form/FormControlVerificationStatus/MultipleSelect';
import { EndorsementOptions } from 'common/document/ui/endorsements';
import { RestrictionOptions } from 'common/document/ui/restrictions';
import { DriverLicenseTypeOptions } from 'common/document/ui/driver-license-type';
import { DatePickerWithStatus } from 'components/Form/FormControlVerificationStatus/DatePicker';
import { SelectWithStatus } from 'components/Form/FormControlVerificationStatus/Select';
import { IFieldPolygon } from '../Utils/field-polygon';
import { IFieldVerificationResult } from '../Utils/verification-result';
import { shouldHideVerificationByStatus } from '../Utils/verification-status';
import React from 'react';
import { TemporaryLicenseCheckBox } from './TemporaryLicenseCheckBox';

type InputProps = {
  getFieldErrorProp: (field: keyof IDriverCdlFields) => {
    error: string | undefined;
  };
  getFieldProps: (field: keyof IDriverCdlFields) => {
    name: keyof IDriverCdlFields;
    value: any;
    onChange: (inputValue: any) => void;
  };
  fieldVerified: { [key: string]: boolean };
  fieldPolygon: IFieldPolygon;
  verificationResult?: IFieldVerificationResult;
  verificationStatus?: any;
  onHoverField?: (polygon: any | null) => void;
  onUnHoverField?: () => void;
  disabled?: boolean;
  containsUserPhoto?: boolean;
  setContainsUserPhoto?: (value: boolean) => void;
};

export const DriverLicenseForm = ({
  getFieldErrorProp,
  getFieldProps,
  fieldVerified,
  fieldPolygon,
  verificationResult,
  onHoverField,
  onUnHoverField,
  verificationStatus,
  disabled = false,
  containsUserPhoto = true,
  setContainsUserPhoto,
}: InputProps) => {
  const handleHoverField = (data: any) => {
    onHoverField && onHoverField(data);
  };

  const handleUnHoverField = () => {
    onUnHoverField && onUnHoverField();
  };

  return (
    <form>
      <FormControlWithStatus
        id="DriversLicenseNumber"
        label="Drivers License Number"
        disabled={disabled}
      >
        <InputWithStatus
          {...getFieldProps('driversLicenseNumber')}
          id="DriversLicenseNumber"
          label="Drivers License Number"
          error={!!getFieldErrorProp('driversLicenseNumber').error || false}
          verified={fieldVerified['driversLicenseNumber']}
          verificationResult={
            verificationResult
              ? verificationResult['driversLicenseNumber']
              : undefined
          }
          hideVerification={shouldHideVerificationByStatus(
            verificationStatus,
            'driversLicenseNumber'
          )}
          onMouseEnter={() =>
            handleHoverField(fieldPolygon['driversLicenseNumber'])
          }
          onMouseLeave={handleUnHoverField}
        />
      </FormControlWithStatus>
      <FormControlWithStatus
        id="FirstName"
        label="First Name"
        disabled={disabled}
      >
        <InputWithStatus
          {...getFieldProps('firstName')}
          id="FirstName"
          label="First Name"
          error={!!getFieldErrorProp('firstName').error || false}
          verified={fieldVerified['firstName']}
          verificationResult={
            verificationResult ? verificationResult['firstName'] : undefined
          }
          hideVerification={shouldHideVerificationByStatus(
            verificationStatus,
            'firstName'
          )}
          onMouseEnter={() => handleHoverField(fieldPolygon['firstName'])}
          onMouseLeave={handleUnHoverField}
        />
      </FormControlWithStatus>
      <FormControlWithStatus
        id="LastName"
        label="Last Name"
        disabled={disabled}
      >
        <InputWithStatus
          {...getFieldProps('lastName')}
          id="LastName"
          label="Last Name"
          error={!!getFieldErrorProp('lastName').error || false}
          verified={fieldVerified['lastName']}
          verificationResult={
            verificationResult ? verificationResult['lastName'] : undefined
          }
          hideVerification={shouldHideVerificationByStatus(
            verificationStatus,
            'lastName'
          )}
          onMouseEnter={() => handleHoverField(fieldPolygon['lastName'])}
          onMouseLeave={handleUnHoverField}
        />
      </FormControlWithStatus>
      <FormControlWithStatus id="DateOfBirth" disabled={disabled}>
        <DatePickerWithStatus
          label="Date of Birth"
          format="MM/dd/yyyy"
          {...getFieldProps('dateOfBirth')}
          verified={fieldVerified['dateOfBirth']}
          verificationResult={
            verificationResult ? verificationResult['dateOfBirth'] : undefined
          }
          disabled={disabled}
          hideVerification={shouldHideVerificationByStatus(
            verificationStatus,
            'dateOfBirth'
          )}
          onMouseEnter={() => handleHoverField(fieldPolygon['dateOfBirth'])}
          onMouseLeave={handleUnHoverField}
        />
      </FormControlWithStatus>
      <Stack direction="row" spacing={1} alignItems="center">
        <FormControlWithStatus id="Class" label="Class" disabled={disabled}>
          <SelectWithStatus
            {...getFieldProps('driverClass')}
            id="Class"
            label="Class"
            error={!!getFieldErrorProp('driverClass').error || false}
            verified={fieldVerified['driverClass']}
            verificationResult={
              verificationResult ? verificationResult['driverClass'] : undefined
            }
            hideVerification={shouldHideVerificationByStatus(
              verificationStatus,
              'driverClass'
            )}
            options={DriverLicenseTypeOptions}
            onMouseEnter={() => handleHoverField(fieldPolygon['driverClass'])}
            onMouseLeave={handleUnHoverField}
          />
        </FormControlWithStatus>
        <FormControlWithStatus id="State" label="State" disabled={disabled}>
          <InputWithStatus
            {...getFieldProps('issuingState')}
            id="State"
            label="State"
            error={!!getFieldErrorProp('issuingState').error || false}
            verified={fieldVerified['issuingState']}
            verificationResult={
              verificationResult
                ? verificationResult['issuingState']
                : undefined
            }
            hideVerification={shouldHideVerificationByStatus(
              verificationStatus,
              'issuingState'
            )}
            onMouseEnter={() => handleHoverField(fieldPolygon['issuingState'])}
            onMouseLeave={handleUnHoverField}
          />
        </FormControlWithStatus>
      </Stack>
      <Stack direction="row" spacing={1} alignItems="center">
        <FormControlWithStatus id="Issued" disabled={disabled}>
          <DatePickerWithStatus
            label="Issued"
            {...getFieldProps('issueDate')}
            verified={fieldVerified['issueDate']}
            verificationResult={
              verificationResult ? verificationResult['issueDate'] : undefined
            }
            disabled={disabled}
            hideVerification={shouldHideVerificationByStatus(
              verificationStatus,
              'issueDate'
            )}
            onMouseEnter={() => handleHoverField(fieldPolygon['issueDate'])}
            onMouseLeave={handleUnHoverField}
          />
        </FormControlWithStatus>
        <FormControlWithStatus id="Expires" disabled={disabled}>
          <DatePickerWithStatus
            label="Expires"
            {...getFieldProps('expireDate')}
            verified={fieldVerified['expireDate']}
            verificationResult={
              verificationResult ? verificationResult['expireDate'] : undefined
            }
            disabled={disabled}
            hideVerification={shouldHideVerificationByStatus(
              verificationStatus,
              'expireDate'
            )}
            onMouseEnter={() => handleHoverField(fieldPolygon['expireDate'])}
            onMouseLeave={handleUnHoverField}
          />
        </FormControlWithStatus>
      </Stack>
      <Stack direction="row" spacing={1} alignItems="center">
        <FormControlWithStatus
          id="Restrictions"
          label="Restrictions"
          disabled={disabled}
        >
          <MultipleSelectWithStatus
            {...getFieldProps('restrictions')}
            id="Restrictions"
            label="Restrictions"
            error={!!getFieldErrorProp('restrictions').error || false}
            options={RestrictionOptions}
            verified={fieldVerified['restrictions']}
            verificationResult={
              verificationResult
                ? verificationResult['restrictions']
                : undefined
            }
            hideVerification={shouldHideVerificationByStatus(
              verificationStatus,
              'restrictions'
            )}
            onMouseEnter={() => handleHoverField(fieldPolygon['restrictions'])}
            onMouseLeave={handleUnHoverField}
          />
        </FormControlWithStatus>
        <FormControlWithStatus
          id="Endorsements"
          label="Endorsements"
          disabled={disabled}
        >
          <MultipleSelectWithStatus
            {...getFieldProps('endorsements')}
            id="Endorsements"
            label="Endorsements"
            error={!!getFieldErrorProp('endorsements').error || false}
            options={EndorsementOptions}
            verified={fieldVerified['endorsements']}
            verificationResult={
              verificationResult
                ? verificationResult['endorsements']
                : undefined
            }
            hideVerification={shouldHideVerificationByStatus(
              verificationStatus,
              'endorsements'
            )}
            onMouseEnter={() => handleHoverField(fieldPolygon['endorsements'])}
            onMouseLeave={handleUnHoverField}
          />
        </FormControlWithStatus>
      </Stack>
      <FormControlWithStatus id="Country" label="Country" disabled={disabled}>
        <InputWithStatus
          {...getFieldProps('country')}
          id="Country"
          label="Country"
          error={!!getFieldErrorProp('country').error || false}
          verified={fieldVerified['country']}
          verificationResult={
            verificationResult ? verificationResult['country'] : undefined
          }
          hideVerification={shouldHideVerificationByStatus(
            verificationStatus,
            'country'
          )}
          onMouseEnter={() => handleHoverField(fieldPolygon['country'])}
          onMouseLeave={handleUnHoverField}
        />
      </FormControlWithStatus>
      {setContainsUserPhoto && (
        <TemporaryLicenseCheckBox
          containsUserPhoto={containsUserPhoto}
          setContainsUserPhoto={setContainsUserPhoto}
          disabled={disabled}
        />
      )}
    </form>
  );
};
